import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProducts } from '../../hooks/useFirestore';
import {
  createProduct,
  updateProduct,
  deleteProduct
} from '../../services/firestoreService';
import { uploadVideoToCloudinary } from '../../config/cloudinary';
import '../../styles/admin.css';

const defaultForm = {
  title: '',
  description: '',
  status: 'coming_soon',
  launchDate: '',
  benefitsText: '',
  imageUrl: ''
};

const ManageProducts = () => {
  const navigate = useNavigate();
  const { data: products = [], loading } = useProducts();
  const productList = Array.isArray(products) ? products : [];
  const totalProducts = productList.length;
  const [formData, setFormData] = useState(defaultForm);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (!saving && feedback) {
      const timeout = setTimeout(() => setFeedback(''), 3000);
      return () => clearTimeout(timeout);
    }
  }, [feedback, saving]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData(defaultForm);
    setEditingProduct(null);
  };

  const formatBenefits = (benefits) =>
    Array.isArray(benefits) ? benefits.join('\n') : (benefits || '');

  const benefitsArray = (text) =>
    text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title || '',
      description: product.description || '',
      status: product.status || 'coming_soon',
      launchDate: product.launchDate || '',
      benefitsText: formatBenefits(product.benefits),
      imageUrl: product.imageUrl || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleImageSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file (PNG, JPG, JPEG, WEBP).');
      return;
    }
    setSelectedImage(file);
  };

  const handleImageUpload = async () => {
    if (!selectedImage) {
      alert('Please choose an image file first.');
      return;
    }

    try {
      setUploadingImage(true);
      setUploadProgress(0);
      const result = await uploadVideoToCloudinary(selectedImage, (progressEvent) => {
        if (progressEvent && progressEvent.loaded && progressEvent.total) {
          setUploadProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100));
        }
      });

      setFormData((prev) => ({ ...prev, imageUrl: result.url }));
      setSelectedImage(null);
      setUploadProgress(100);
      setFeedback('Image uploaded successfully!');
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Image upload failed. Please try again.');
    } finally {
      setUploadingImage(false);
      setTimeout(() => setUploadProgress(0), 2000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert('Please enter a product name.');
      return;
    }

    setSaving(true);
    const payload = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      status: formData.status,
      launchDate: formData.launchDate.trim(),
      benefits: benefitsArray(formData.benefitsText),
      imageUrl: formData.imageUrl
    };

    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, payload);
        setFeedback('Product updated successfully.');
      } else {
        await createProduct(payload);
        setFeedback('Product created successfully.');
      }
      resetForm();
    } catch (error) {
      console.error('Product save error:', error);
      alert('Failed to save product. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await deleteProduct(productId);
      setFeedback('Product deleted.');
    } catch (error) {
      console.error('Product delete error:', error);
      alert('Failed to delete product.');
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-left">
          <button onClick={() => navigate('/admin')} className="back-btn">
            ← Back to Dashboard
          </button>
          <h1 className="admin-title">Manage Products</h1>
        </div>
        <button onClick={() => navigate(-1)} className="btn btn-secondary outline">
          Go Back
        </button>
      </div>

      <div className="admin-container">
        {feedback && <div className="info-banner success">{feedback}</div>}
        <div className="admin-card">
          <h2 className="card-title">
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </h2>

          <form className="admin-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Product Title *</label>
              <input
                id="title"
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Enter product name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                rows="3"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe the product or reward"
              />
            </div>

            <div className="form-grid-2">
              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                >
                  <option value="coming_soon">Coming Soon</option>
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="launchDate">Launch Date</label>
                <input
                  id="launchDate"
                  type="date"
                  value={formData.launchDate}
                  onChange={(e) => handleChange('launchDate', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="benefits">Benefits (one per line)</label>
              <textarea
                id="benefits"
                rows="4"
                value={formData.benefitsText}
                onChange={(e) => handleChange('benefitsText', e.target.value)}
                placeholder="Eg. Exclusive VIP merchandise"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="imageUpload">Product Image</label>
              <div className="image-upload-field">
                <input
                  id="imageUpload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  disabled={uploadingImage}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleImageUpload}
                  disabled={!selectedImage || uploadingImage}
                >
                  {uploadingImage ? `Uploading ${uploadProgress}%` : 'Upload to Cloudinary'}
                </button>
              </div>
              <input
                type="url"
                placeholder="https://cdn.domain.com/product-image.jpg"
                value={formData.imageUrl}
                onChange={(e) => handleChange('imageUrl', e.target.value)}
              />
              {formData.imageUrl && (
                <div className="product-image-preview">
                  <img src={formData.imageUrl} alt="Product preview" />
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => handleChange('imageUrl', '')}
                  >
                    Remove image
                  </button>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : editingProduct ? 'Update Product' : 'Add Product'}
              </button>
              {editingProduct && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={resetForm}
                  disabled={saving}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="admin-card">
          <div className="card-title">Products ({totalProducts})</div>

          {loading ? (
            <div className="loading-text">Loading products...</div>
          ) : totalProducts === 0 ? (
            <div className="empty-state">
              <p>No products added yet. Use the form above to add one.</p>
            </div>
          ) : (
            <div className="videos-grid">
              {productList.map((product) => (
                <div key={product.id} className="video-item">
                  <div className="product-item-header">
                    <h3>{product.title}</h3>
                    <span className={`status-chip status-${product.status || 'coming_soon'}`}>
                      {(product.status || 'coming_soon').replace('_', ' ')}
                    </span>
                  </div>
                  {product.description && <p>{product.description}</p>}
                  {product.launchDate && (
                    <p className="text-sm text-gray-500">
                      Launches on {product.launchDate}
                    </p>
                  )}
                  {product.imageUrl && (
                    <div className="product-item-image">
                      <img src={product.imageUrl} alt={product.title} />
                    </div>
                  )}
                  {product.benefits && product.benefits.length > 0 && (
                    <ul className="product-benefits-list">
                      {product.benefits.map((benefit, idx) => (
                        <li key={idx}>{benefit}</li>
                      ))}
                    </ul>
                  )}
                  <div className="video-actions">
                    <button className="btn-edit" onClick={() => handleEdit(product)}>
                      Edit
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(product.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageProducts;
